import React, { useState, useEffect } from 'react';
import { BuildingUsageRecordFormData, PortvilleBuildingUsageRecord } from '../../../../types/portvilleBilling';

interface BuildingUsageInputFormProps {
  selectedRecord?: PortvilleBuildingUsageRecord | null;
  onSave: (data: BuildingUsageRecordFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  selectedYear: number;
  selectedMonth: number;
}

const BuildingUsageInputForm: React.FC<BuildingUsageInputFormProps> = ({
  selectedRecord,
  onSave,
  onCancel,
  loading = false,
  selectedYear,
  selectedMonth
}) => {
  const [formData, setFormData] = useState<BuildingUsageRecordFormData>({
    year: selectedYear,
    month: selectedMonth,
    totalElectricityFee: 0,
    buildingIndividualUsage: 0,
    buildingCommonUsage: 0,
    totalBaseFee: 0,
    totalOffsetFee: 0,
    totalElectricityFund: 0,
    portvilleTotalWaterFee: 0,
    portvilleBuildingWaterIndividualUsage: 0,
    portvilleBuildingWaterCommonUsage: 0,
    neulhaerangTotalWaterFee: 0,
    neulhaerangBuildingWaterIndividualUsage: 0,
    neulhaerangBuildingWaterCommonUsage: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusedFields, setFocusedFields] = useState<Set<string>>(new Set());

  // 편집 모드일 때 기존 데이터로 폼 초기화
  useEffect(() => {
    if (selectedRecord) {
      setFormData({
        year: (selectedRecord as any).period?.year || (selectedRecord as any).year,
        month: (selectedRecord as any).period?.month || (selectedRecord as any).month,
        totalElectricityFee: selectedRecord.totalElectricityFee,
        buildingIndividualUsage: selectedRecord.buildingIndividualUsage,
        buildingCommonUsage: selectedRecord.buildingCommonUsage,
        totalBaseFee: selectedRecord.totalBaseFee,
        totalOffsetFee: selectedRecord.totalOffsetFee,
        totalElectricityFund: selectedRecord.totalElectricityFund,
        // 포트빌 수도 (기존 데이터는 포트빌로 간주)
        portvilleTotalWaterFee: (selectedRecord as any).portvilleTotalWaterFee || selectedRecord.totalWaterFee,
        portvilleBuildingWaterIndividualUsage: (selectedRecord as any).portvilleBuildingWaterIndividualUsage || selectedRecord.buildingWaterIndividualUsage,
        portvilleBuildingWaterCommonUsage: (selectedRecord as any).portvilleBuildingWaterCommonUsage || selectedRecord.buildingWaterCommonUsage,
        // 늘해랑 수도 (기본값 0)
        neulhaerangTotalWaterFee: (selectedRecord as any).neulhaerangTotalWaterFee || 0,
        neulhaerangBuildingWaterIndividualUsage: (selectedRecord as any).neulhaerangBuildingWaterIndividualUsage || 0,
        neulhaerangBuildingWaterCommonUsage: (selectedRecord as any).neulhaerangBuildingWaterCommonUsage || 0
      });
    } else {
      // 새로 추가할 때는 현재 선택된 연/월로 초기화
      setFormData(prev => ({
        ...prev,
        year: selectedYear,
        month: selectedMonth
      }));
    }
  }, [selectedRecord, selectedYear, selectedMonth]);

  const updateFormData = (field: keyof BuildingUsageRecordFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // 해당 필드의 오류 제거
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // 연도 검사
    if (!formData.year || formData.year < 2020 || formData.year > 2030) {
      newErrors.year = '연도는 2020~2030 사이여야 합니다.';
    }

    // 월 검사
    if (!formData.month || formData.month < 1 || formData.month > 12) {
      newErrors.month = '월은 1~12 사이여야 합니다.';
    }

    // 전기 관련 검사
    if (formData.totalElectricityFee < 0) {
      newErrors.totalElectricityFee = '전체전력량요금은 0 이상이어야 합니다.';
    }
    if (formData.buildingIndividualUsage < 0) {
      newErrors.buildingIndividualUsage = '건물 개별사용량은 0 이상이어야 합니다.';
    }
    // 건물 공동사용량은 음수 허용 (측정에 따라 음수가 될 수 있음)
    if (formData.totalBaseFee < 0) {
      newErrors.totalBaseFee = '전체기본요금은 0 이상이어야 합니다.';
    }
    // 전체상계거래요금은 음수 허용 (상계 거래로 인한 음수 발생 가능)
    if (formData.totalElectricityFund < 0) {
      newErrors.totalElectricityFund = '전체전력기금은 0 이상이어야 합니다.';
    }

    // 포트빌 수도 관련 검사
    if (formData.portvilleTotalWaterFee < 0) {
      newErrors.portvilleTotalWaterFee = '포트빌 전체수도요금은 0 이상이어야 합니다.';
    }
    if (formData.portvilleBuildingWaterIndividualUsage < 0) {
      newErrors.portvilleBuildingWaterIndividualUsage = '포트빌 건물개별사용량은 0 이상이어야 합니다.';
    }

    // 늘해랑 수도 관련 검사
    if (formData.neulhaerangTotalWaterFee < 0) {
      newErrors.neulhaerangTotalWaterFee = '늘해랑 전체수도요금은 0 이상이어야 합니다.';
    }
    if (formData.neulhaerangBuildingWaterIndividualUsage < 0) {
      newErrors.neulhaerangBuildingWaterIndividualUsage = '늘해랑 건물개별사용량은 0 이상이어야 합니다.';
    }
    // 건물공동사용량은 음수 허용 (측정에 따라 음수가 될 수 있음)

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData);
    } catch (error) {
      console.error('건물 전체 사용량 저장 실패:', error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {selectedRecord ? '건물 전체 사용량 수정' : '건물 전체 사용량 입력'}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          월별 건물 전체 전기/수도 요금 및 사용량을 입력하세요.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 기간 설정 */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">기간 설정</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                연도 *
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => updateFormData('year', parseInt(e.target.value) || 0)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  errors.year ? 'border-red-500' : 'border-gray-300'
                }`}
                min="2020"
                max="2030"
                required
              />
              {errors.year && <p className="mt-1 text-sm text-red-600">{errors.year}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                월 *
              </label>
              <select
                value={formData.month}
                onChange={(e) => updateFormData('month', parseInt(e.target.value))}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                  errors.month ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>{month}월</option>
                ))}
              </select>
              {errors.month && <p className="mt-1 text-sm text-red-600">{errors.month}</p>}
            </div>
          </div>
        </div>

        {/* 전기 관련 */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">전기 관련</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  전체전력량요금 (원) *
                </label>
                <input
                  type="number"
                  value={formData.totalElectricityFee !== undefined ? formData.totalElectricityFee.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('totalElectricityFee', value === '' ? undefined : (parseInt(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'totalElectricityFee']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('totalElectricityFee');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('totalElectricityFee') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.totalElectricityFee ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="numeric"
                  step="100"
                  required
                />
                {errors.totalElectricityFee && <p className="mt-1 text-sm text-red-600">{errors.totalElectricityFee}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  전체기본요금 (원) *
                </label>
                <input
                  type="number"
                  value={formData.totalBaseFee !== undefined ? formData.totalBaseFee.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('totalBaseFee', value === '' ? undefined : (parseInt(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'totalBaseFee']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('totalBaseFee');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('totalBaseFee') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.totalBaseFee ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="numeric"
                  step="100"
                  required
                />
                {errors.totalBaseFee && <p className="mt-1 text-sm text-red-600">{errors.totalBaseFee}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  건물 개별사용량 (kWh) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.buildingIndividualUsage !== undefined ? formData.buildingIndividualUsage.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('buildingIndividualUsage', value === '' ? undefined : (parseFloat(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'buildingIndividualUsage']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('buildingIndividualUsage');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('buildingIndividualUsage') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.buildingIndividualUsage ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="decimal"
                  required
                />
                {errors.buildingIndividualUsage && <p className="mt-1 text-sm text-red-600">{errors.buildingIndividualUsage}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  건물 공동사용량 (kWh) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.buildingCommonUsage !== undefined ? formData.buildingCommonUsage.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('buildingCommonUsage', value === '' ? undefined : (parseFloat(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'buildingCommonUsage']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('buildingCommonUsage');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('buildingCommonUsage') ? '' : '0 (음수 허용)'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.buildingCommonUsage ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="decimal"
                  required
                />
                {errors.buildingCommonUsage && <p className="mt-1 text-sm text-red-600">{errors.buildingCommonUsage}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  전체상계거래요금 (원) *
                </label>
                <input
                  type="number"
                  value={formData.totalOffsetFee !== undefined ? formData.totalOffsetFee.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('totalOffsetFee', value === '' ? undefined : (parseInt(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'totalOffsetFee']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('totalOffsetFee');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('totalOffsetFee') ? '' : '0 (음수 허용)'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.totalOffsetFee ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="numeric"
                  step="100"
                  required
                />
                {errors.totalOffsetFee && <p className="mt-1 text-sm text-red-600">{errors.totalOffsetFee}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  전체전력기금 (원) *
                </label>
                <input
                  type="number"
                  value={formData.totalElectricityFund !== undefined ? formData.totalElectricityFund.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('totalElectricityFund', value === '' ? undefined : (parseInt(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'totalElectricityFund']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('totalElectricityFund');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('totalElectricityFund') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.totalElectricityFund ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="numeric"
                  step="100"
                  required
                />
                {errors.totalElectricityFund && <p className="mt-1 text-sm text-red-600">{errors.totalElectricityFund}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* 포트빌 수도 관련 */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">포트빌 수도 관련</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  포트빌 전체수도요금 (원) *
                </label>
                <input
                  type="number"
                  value={formData.portvilleTotalWaterFee !== undefined ? formData.portvilleTotalWaterFee.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('portvilleTotalWaterFee', value === '' ? undefined : (parseInt(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'portvilleTotalWaterFee']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('portvilleTotalWaterFee');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('portvilleTotalWaterFee') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.portvilleTotalWaterFee ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="numeric"
                  step="100"
                  required
                />
                {errors.portvilleTotalWaterFee && <p className="mt-1 text-sm text-red-600">{errors.portvilleTotalWaterFee}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  포트빌 건물개별사용량 (톤) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.portvilleBuildingWaterIndividualUsage !== undefined ? formData.portvilleBuildingWaterIndividualUsage.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('portvilleBuildingWaterIndividualUsage', value === '' ? undefined : (parseFloat(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'portvilleBuildingWaterIndividualUsage']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('portvilleBuildingWaterIndividualUsage');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('portvilleBuildingWaterIndividualUsage') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.portvilleBuildingWaterIndividualUsage ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="decimal"
                  required
                />
                {errors.portvilleBuildingWaterIndividualUsage && <p className="mt-1 text-sm text-red-600">{errors.portvilleBuildingWaterIndividualUsage}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  포트빌 건물공동사용량 (톤) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.portvilleBuildingWaterCommonUsage !== undefined ? formData.portvilleBuildingWaterCommonUsage.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('portvilleBuildingWaterCommonUsage', value === '' ? undefined : (parseFloat(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'portvilleBuildingWaterCommonUsage']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('portvilleBuildingWaterCommonUsage');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('portvilleBuildingWaterCommonUsage') ? '' : '0 (음수 허용)'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.portvilleBuildingWaterCommonUsage ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="decimal"
                  required
                />
                {errors.portvilleBuildingWaterCommonUsage && <p className="mt-1 text-sm text-red-600">{errors.portvilleBuildingWaterCommonUsage}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* 늘해랑 수도 관련 */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">늘해랑 수도 관련</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  늘해랑 전체수도요금 (원) *
                </label>
                <input
                  type="number"
                  value={formData.neulhaerangTotalWaterFee !== undefined ? formData.neulhaerangTotalWaterFee.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('neulhaerangTotalWaterFee', value === '' ? undefined : (parseInt(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'neulhaerangTotalWaterFee']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('neulhaerangTotalWaterFee');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('neulhaerangTotalWaterFee') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.neulhaerangTotalWaterFee ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="numeric"
                  step="100"
                  required
                />
                {errors.neulhaerangTotalWaterFee && <p className="mt-1 text-sm text-red-600">{errors.neulhaerangTotalWaterFee}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  늘해랑 건물개별사용량 (톤) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.neulhaerangBuildingWaterIndividualUsage !== undefined ? formData.neulhaerangBuildingWaterIndividualUsage.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('neulhaerangBuildingWaterIndividualUsage', value === '' ? undefined : (parseFloat(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'neulhaerangBuildingWaterIndividualUsage']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('neulhaerangBuildingWaterIndividualUsage');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('neulhaerangBuildingWaterIndividualUsage') ? '' : '0'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.neulhaerangBuildingWaterIndividualUsage ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="decimal"
                  required
                />
                {errors.neulhaerangBuildingWaterIndividualUsage && <p className="mt-1 text-sm text-red-600">{errors.neulhaerangBuildingWaterIndividualUsage}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  늘해랑 건물공동사용량 (톤) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.neulhaerangBuildingWaterCommonUsage !== undefined ? formData.neulhaerangBuildingWaterCommonUsage.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateFormData('neulhaerangBuildingWaterCommonUsage', value === '' ? undefined : (parseFloat(value) || 0));
                  }}
                  onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'neulhaerangBuildingWaterCommonUsage']))}
                  onBlur={() => setFocusedFields(prev => {
                    const newSet = new Set(prev);
                    newSet.delete('neulhaerangBuildingWaterCommonUsage');
                    return newSet;
                  })}
                  placeholder={focusedFields.has('neulhaerangBuildingWaterCommonUsage') ? '' : '0 (음수 허용)'}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    errors.neulhaerangBuildingWaterCommonUsage ? 'border-red-500' : 'border-gray-300'
                  }`}
                  inputMode="decimal"
                  required
                />
                {errors.neulhaerangBuildingWaterCommonUsage && <p className="mt-1 text-sm text-red-600">{errors.neulhaerangBuildingWaterCommonUsage}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            취소
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? '저장 중...' : (selectedRecord ? '수정' : '저장')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BuildingUsageInputForm;
